import React from 'react';
import BlogPost from '../BlogPost';

export default function BlogSIRSBaselineFunding() {
  return (
    <BlogPost
      title="How to Calculate a Compliant SIRS Baseline Funding Plan in Florida (2026 Step-by-Step Guide)"
      date="May 2026"
      readTime="12 min read"
    >
      <p><strong>Your engineering firm delivered a 40-page SIRS report. Now what?</strong></p>

      <p>You have component costs. You have remaining useful life estimates. You have replacement cost projections. But your board is asking a single question you cannot yet answer:</p>

      <p><em>"How much do we need to contribute every month to stay compliant with HB 913 — without a surprise special assessment ten years from now?"</em></p>

      <p>That answer lives inside the baseline funding plan. And right now, almost no plain-English guidance exists on how to actually build one.</p>

      <p>This guide fixes that. We will walk through every step — from reading your engineering report to producing a 30-year cash flow projection that satisfies Florida's <strong>§718.112(2)(g)</strong> mandate under HB 913.</p>

      <p>No accounting degree required. No $5,000 consultant. Just a clear process your board treasurer can follow.</p>

      <hr />

      <h2>What Is a SIRS Baseline Funding Plan? (And Why It Matters)</h2>

      <p>Florida's Structural Integrity Reserve Study (SIRS) law — most recently updated by <strong>House Bill 913, effective July 1, 2025</strong> — requires every condominium association with a building of three or more habitable stories to maintain reserves for eight specific structural components.</p>

      <p>But having a reserve fund is not enough. Under HB 913, your reserves must be governed by a <strong>baseline funding plan</strong>: a 30-year projection that proves your reserve cash balance <em>never drops below zero dollars</em> in any year of the projection window.</p>

      <p>This is a precise legal standard, not a general recommendation. If your projection shows a negative balance in year 14 — even if you recover in year 15 — you are not in compliance.</p>

      <p>The baseline funding plan is what separates a compliant association from one that is exposed to DBPR enforcement, board member personal liability, and the kind of special assessments that make front-page news in Miami-Dade and Broward counties.</p>

      <blockquote style={{ borderLeft: '4px solid #2A9D8F', paddingLeft: 20, margin: '24px 0', color: '#374151' }}>
        <p><strong>Key definition:</strong> A baseline funding plan maintains reserve balances at or above $0 for all 8 mandated SIRS components over a 30-year projection period, as required by §718.112(2)(g) of the Florida Statutes.</p>
      </blockquote>

      <hr />

      <h2>The 8 Mandatory SIRS Components You Must Fund</h2>

      <p>Before you can build a baseline funding plan, you need to know what you are funding. HB 913 mandates reserves for eight specific structural components. Your engineering report should identify and cost each of these:</p>

      <ol>
        <li><strong>Roof</strong> — Replacement of the entire roofing system, including membrane, decking, and flashing</li>
        <li><strong>Load-bearing structure</strong> — Primary structural members: columns, beams, load-bearing walls, and foundation elements</li>
        <li><strong>Fire protection systems</strong> — Sprinklers, standpipes, fire alarm panels, and suppression infrastructure</li>
        <li><strong>Plumbing</strong> — Main supply lines, waste stacks, and drainage systems serving the building</li>
        <li><strong>Electrical systems</strong> — Main distribution panels, wiring infrastructure, and building-wide electrical systems</li>
        <li><strong>Waterproofing and exterior painting</strong> — Balcony waterproofing, exterior coatings, sealants, and caulking</li>
        <li><strong>Windows and exterior doors</strong> — All windows and exterior-facing doors that affect building envelope integrity</li>
        <li><strong>Any other item exceeding $25,000</strong> whose failure would directly affect the structural integrity of the components listed above</li>
      </ol>

      <p>Your engineering firm should have assigned each component a <strong>remaining useful life (RUL)</strong> and a <strong>replacement cost estimate</strong>. These two numbers are your inputs for every calculation that follows.</p>

      <p>If your report is missing any of the eight components, or if a component's remaining useful life or replacement cost is listed as "not applicable" without explanation, push back on your engineer before you proceed. A missing component is a compliance gap.</p>

      <hr />

      <h2>Step 1: Extract the Core Numbers from Your SIRS Report</h2>

      <p>Open your SIRS engineering report and build a simple table. For every component, you need four numbers:</p>

      <div style={{ overflowX: 'auto', margin: '24px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
          <thead>
            <tr style={{ background: '#1B3A5C', color: '#fff' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Component</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Current Reserve Balance</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Remaining Useful Life</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Replacement Cost</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Annual Contribution</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '10px 14px' }}>Roof</td>
              <td style={{ padding: '10px 14px' }}>$45,000</td>
              <td style={{ padding: '10px 14px' }}>8 years</td>
              <td style={{ padding: '10px 14px' }}>$380,000</td>
              <td style={{ padding: '10px 14px', color: '#6b7280' }}><em>To be calculated</em></td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <td style={{ padding: '10px 14px' }}>Load-bearing structure</td>
              <td style={{ padding: '10px 14px' }}>$0</td>
              <td style={{ padding: '10px 14px' }}>25 years</td>
              <td style={{ padding: '10px 14px' }}>$1,200,000</td>
              <td style={{ padding: '10px 14px', color: '#6b7280' }}><em>To be calculated</em></td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '10px 14px' }}>Waterproofing</td>
              <td style={{ padding: '10px 14px' }}>$12,000</td>
              <td style={{ padding: '10px 14px' }}>5 years</td>
              <td style={{ padding: '10px 14px' }}>$95,000</td>
              <td style={{ padding: '10px 14px', color: '#6b7280' }}><em>To be calculated</em></td>
            </tr>
            <tr style={{ borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <td style={{ padding: '10px 14px', color: '#6b7280' }}><em>... repeat for all 8 components</em></td>
              <td style={{ padding: '10px 14px' }}></td>
              <td style={{ padding: '10px 14px' }}></td>
              <td style={{ padding: '10px 14px' }}></td>
              <td style={{ padding: '10px 14px' }}></td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>The <strong>current reserve balance</strong> is the money already sitting in your reserve fund that is earmarked for each component. Many associations have $0 in component-specific reserves because they have been deferring reserve funding for years — which is exactly why HB 913 exists.</p>

      <p>If your association does not break down reserves by component, use your total reserve balance and allocate it proportionally based on each component's replacement cost. Your engineer or accountant can help with this allocation if needed.</p>

      <hr />

      <h2>Step 2: Calculate the Annual Contribution for Each Component</h2>

      <p>This is the core calculation. For each SIRS component, you need to determine how much to set aside <em>per year</em> so that the money is available when the component needs to be replaced.</p>

      <p>The straight-line method is the standard approach for baseline funding. The formula is:</p>

      <blockquote style={{ borderLeft: '4px solid #2A9D8F', paddingLeft: 20, margin: '24px 0', color: '#374151' }}>
        <p><strong>Annual Contribution = (Replacement Cost − Current Balance) ÷ Remaining Useful Life</strong></p>
      </blockquote>

      <p>Let us work through the roof example from the table above:</p>

      <ul>
        <li>Replacement cost: $380,000</li>
        <li>Current balance: $45,000</li>
        <li>Remaining useful life: 8 years</li>
      </ul>

      <p><strong>Annual contribution = ($380,000 − $45,000) ÷ 8 = $335,000 ÷ 8 = $41,875 per year</strong></p>

      <p>That is $3,489 per month across the entire association just for the roof component. Divide by your number of units to find the per-unit monthly cost.</p>

      <p>Run this calculation for all eight components. Sum the annual contributions. That total is your baseline annual reserve deposit.</p>

      <p><strong>A critical note on inflation:</strong> Your engineering report's replacement costs are today's prices. By the time your roof needs replacement in eight years, inflation will have increased that cost. HB 913 does not specify an inflation rate, but most reserve analysts use 3–4% per year as a conservative assumption for construction cost inflation in South Florida. If you apply inflation adjustments, increase your replacement cost estimate accordingly — which will increase your required annual contribution.</p>

      <hr />

      <h2>Step 3: Build the 30-Year Cash Flow Projection</h2>

      <p>This is where most boards get stuck. Calculating a single annual contribution is straightforward. Proving that contributions plus interest minus expenditures never results in a negative balance <em>across 30 years</em> — that is the baseline funding plan.</p>

      <p>Here is the structure of your year-by-year projection table:</p>

      <div style={{ overflowX: 'auto', margin: '24px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
          <thead>
            <tr style={{ background: '#1B3A5C', color: '#fff' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Year</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Opening Balance</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Annual Contribution</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Interest Earned</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Expenditures</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Closing Balance</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Compliant?</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['1', '$57,000', '$94,500', '$1,710', '$0', '$153,210', '✓ Yes'],
              ['2', '$153,210', '$94,500', '$4,596', '$0', '$252,306', '✓ Yes'],
              ['...', '', '', '', '', '', ''],
              ['8', '$641,000', '$94,500', '$19,230', '$380,000 (roof)', '$374,730', '✓ Yes'],
              ['9', '$374,730', '$94,500', '$11,241', '$0', '$480,471', '✓ Yes'],
            ].map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #e5e7eb', background: i % 2 === 1 ? '#f9fafb' : '#fff' }}>
                {row.map((cell, j) => (
                  <td key={j} style={{ padding: '10px 14px', color: cell === '✓ Yes' ? '#059669' : '#1f2937', fontWeight: cell === '✓ Yes' ? 600 : 400 }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>The most important column is <strong>Closing Balance</strong>. It must be $0 or greater in every single row for 30 years. The moment it goes negative — even briefly — you have a baseline funding plan failure. You need to increase contributions, add a special assessment in that year, or secure a line of credit to cover the shortfall.</p>

      <p>For interest earned, use a conservative rate reflecting your actual reserve account yield. Many Florida associations hold reserves in high-yield savings accounts or money market funds. A rate of 3–5% is defensible in the current rate environment, but use your actual account rate for the projection.</p>

      <hr />

      <h2>Step 4: Identify the Problem Years</h2>

      <p>Once you have built your 30-year projection, look for years where the closing balance drops dangerously close to zero. These are your "pinch points" — years when a major replacement coincides with a low accumulated balance.</p>

      <p>Common pinch points in Florida condo SIRS plans:</p>

      <ul>
        <li><strong>Roof replacement years</strong> — Typically every 15–25 years for South Florida roofs, depending on material</li>
        <li><strong>Waterproofing cycles</strong> — Balcony and exterior waterproofing often cycles every 10–15 years</li>
        <li><strong>Elevator and mechanical</strong> — Often captured under the "other items exceeding $25,000" component</li>
        <li><strong>Window replacement</strong> — Can be a single large expenditure for buildings with 100+ units</li>
      </ul>

      <p>When two major expenditures fall within two to three years of each other, the accumulated balance may not be sufficient to cover both without a shortfall. That is when you need to model alternatives: a moderate assessment increase in the years leading up to the pinch point, a special assessment in the pinch year, or a reserve line of credit.</p>

      <hr />

      <h2>Step 5: Model Funding Alternatives (HB 913 Allows Three)</h2>

      <p>Here is something many boards do not know: <strong>HB 913 explicitly allows alternatives to straight-line cash reserve accumulation.</strong> Under §718.112(2)(g), associations can use any combination of three funding mechanisms — as long as the baseline constraint (never below $0) is satisfied:</p>

      <ol>
        <li><strong>Monthly reserve contributions</strong> — The standard approach. Ongoing monthly assessments deposited into the reserve fund.</li>
        <li><strong>Special assessments</strong> — A one-time charge to all unit owners, requiring a majority vote under HB 913. Can be used to close a projected gap in a specific year.</li>
        <li><strong>Loans and lines of credit</strong> — The association can borrow to fund reserve expenditures, with a majority owner vote required. The loan repayment then replaces or supplements reserve contributions.</li>
      </ol>

      <p>A blended approach is often the most financially realistic option for associations that are starting from a low reserve balance. For example: increase monthly contributions by $75 per unit per month for four years, then use a line of credit to bridge a roof replacement gap in year five, and repay the line over the following six years through a modest surcharge.</p>

      <p>The key is that your projection must show how the blended approach keeps the balance above $0 in every year. If you model a loan, add the loan proceeds as income in the year drawn and the annual repayment as an expenditure in subsequent years.</p>

      <hr />

      <h2>Step 6: Document the Plan for DBPR Reporting</h2>

      <p>Under HB 913, your completed SIRS — including the baseline funding plan — must be submitted to the DBPR electronically within <strong>45 days</strong> of completion. Board officers must sign an affidavit confirming receipt of the report.</p>

      <p>Your baseline funding plan documentation should include:</p>

      <ul>
        <li>The completed 30-year cash flow table showing all 8 component cash flows and aggregate balance</li>
        <li>The assumptions used: inflation rate, interest rate, replacement cost source</li>
        <li>The funding mechanism(s) selected and the owner vote record if special assessments or loans are used</li>
        <li>The annual reserve contribution amount translated into a per-unit monthly assessment figure</li>
        <li>The board resolution adopting the funding plan</li>
      </ul>

      <p>If your association has 25 or more units, you are also required under HB 913 to maintain a <strong>website or app</strong> where owners can access official records — including the SIRS report and the baseline funding plan. This requirement became effective January 1, 2026. A shared Google Drive folder does not satisfy this requirement; you need a dedicated owner-facing access page.</p>

      <hr />

      <h2>Common Mistakes That Make a Baseline Funding Plan Non-Compliant</h2>

      <p><strong>Mistake 1: Using the wrong useful life figure.</strong> The remaining useful life in your SIRS report is the engineer's estimate of how many years until the component needs replacement from today. Do not confuse it with the total useful life of the component (how long it lasts from new). Use the remaining useful life in your calculation.</p>

      <p><strong>Mistake 2: Pooling all eight components into a single reserve fund without tracking individual balances.</strong> HB 913 requires SIRS reserves to be kept separate from operating funds and from non-SIRS reserves. While you do not necessarily need eight separate bank accounts, you must track each component's balance independently in your accounting records.</p>

      <p><strong>Mistake 3: Ignoring the $25,000 threshold for Component 8.</strong> The "any other item exceeding $25,000" component captures everything from generator systems to pool structural elements to parking garage coatings. If your building has a significant capital item not covered by the first seven components whose failure would affect structural integrity, it belongs in your SIRS.</p>

      <p><strong>Mistake 4: Treating the baseline funding plan as a one-time document.</strong> Your SIRS must be updated at least every 10 years, and your funding plan should be recalibrated annually to reflect actual reserve spending, changes in interest rates, and construction cost inflation.</p>

      <p><strong>Mistake 5: Assuming a negative balance is acceptable if it recovers quickly.</strong> It is not. The baseline constraint is absolute — zero or above, every year, for 30 years. A single negative year makes the plan non-compliant regardless of what follows.</p>

      <hr />

      <h2>What Happens If Your Association Is Non-Compliant?</h2>

      <p>The DBPR received 10 new investigator positions under HB 913 specifically to enforce SIRS compliance. Non-compliant associations face:</p>

      <ul>
        <li>DBPR investigation and mandatory correction orders</li>
        <li>Civil penalties for ongoing non-compliance</li>
        <li>Personal liability exposure for board members who approved a non-compliant budget</li>
        <li>Loss of Fannie Mae and Freddie Mac loan eligibility for unit sales — which can effectively freeze the resale market in your building</li>
        <li>The very special assessments you were trying to avoid, now made worse by the cost of legal defense and DBPR compliance proceedings</li>
      </ul>

      <p>Board members who can demonstrate they followed a documented, reasonable process — including using a proper baseline funding plan — have meaningful protection under Florida's business judgment rule. Board members who cannot show any compliance process have no such protection.</p>

      <hr />

      <h2>The Faster Path: Using a Tool Built for This Calculation</h2>

      <p>Building a 30-year, eight-component baseline funding plan in a spreadsheet is possible. It takes most board treasurers 8–20 hours, and the risk of a formula error is real. A single incorrect cell reference in a cash flow model can produce a false "compliant" result that collapses under DBPR scrutiny.</p>

      <p>FloridaSIRS.org was built specifically to eliminate this risk. You upload your SIRS engineering report PDF, the platform extracts the eight components with their costs and useful life figures, and you review the extracted data before the calculation runs. The 30-year baseline funding plan generates automatically, with the constraint that the balance never drops below $0 enforced mathematically.</p>

      <p>If you have your SIRS report in hand, you can have a compliant baseline funding plan in under an hour.</p>

      <p><strong>→ <a href="/app" style={{ color: '#2A9D8F' }}>Start with your SIRS report at FloridaSIRS.org</a></strong></p>

      <hr />

      <h2>Frequently Asked Questions</h2>

      <h3>What is the difference between baseline funding and full funding?</h3>
      <p>Baseline funding maintains reserve balances at or above $0 over the 30-year projection — this is the minimum legal standard under HB 913. Full funding means targeting a reserve balance equal to the theoretical ideal balance for each component. Full funding results in higher reserve balances and lower risk of shortfalls. HB 913 requires baseline funding as the minimum; full funding is the gold standard.</p>

      <h3>Can we use money from our operating fund to cover a SIRS reserve shortfall?</h3>
      <p>No. Under HB 913, SIRS reserves must be maintained separately from operating funds. They cannot be borrowed from, redirected, or waived. Attempting to use operating funds to cover a SIRS reserve shortfall is a statutory violation.</p>

      <h3>Do we need a licensed reserve specialist to prepare the baseline funding plan?</h3>
      <p>No. The SIRS itself must be prepared by a licensed engineer or architect. The baseline funding plan is a financial planning document — there is no licensing requirement to prepare it. It is a calculation, not a professional opinion on structural condition.</p>

      <h3>What if our engineer's SIRS report does not include all 8 components?</h3>
      <p>Contact your engineering firm and request a supplemental report or written explanation of any excluded component. "Not applicable" is only acceptable if the building genuinely does not have that system. The engineer must document the reasoning.</p>

      <h3>How often does the baseline funding plan need to be updated?</h3>
      <p>The SIRS itself must be updated every 10 years at a minimum. Your baseline funding plan should be recalibrated annually as part of your budget process to reflect actual reserve spending, updated cost estimates, and changes in interest rates.</p>

      <hr />

      <h2>Summary: The 6-Step Process</h2>

      <ol>
        <li>Extract the four core numbers for each SIRS component: current balance, remaining useful life, replacement cost, and inflation assumptions</li>
        <li>Calculate the annual contribution needed for each component using the straight-line formula</li>
        <li>Build a 30-year year-by-year cash flow table combining contributions, interest, and expenditures</li>
        <li>Verify the closing balance is $0 or above in every year — find and address pinch points</li>
        <li>Model blended funding scenarios if the straight-line approach produces unacceptable assessment increases</li>
        <li>Document the plan, adopt it by board resolution, and submit to DBPR within 45 days of SIRS completion</li>
      </ol>

      <p>Florida's SIRS compliance landscape is complex, but the mathematics of a baseline funding plan are not. The challenge is organizing the data correctly, running the projection without errors, and presenting the results in a way that a board can adopt and unit owners can understand.</p>

      <p>That is exactly what FloridaSIRS.org was built to do.</p>

    </BlogPost>
  );
}
